import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Money,
  Plus,
  CircleNotch,
  User,
  Clock,
  Calendar,
  Download,
  FunnelSimple,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';


export default function PayrollPage() {
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employees');
  const [showForm, setShowForm] = useState(false);
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', role: 'Technician', hourly_rate: 0, phone: '', email: '' });
  const [timeForm, setTimeForm] = useState({ employee_id: '', date: new Date().toISOString().split('T')[0], hours: 0, description: '', client_id: '', billable: true });
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchEmployees();
    fetchTimeEntries();
    fetchClients();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payroll/employees`, { withCredentials: true });
      setEmployees(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchTimeEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/payroll/time-entries`, { withCredentials: true });
      setTimeEntries(response.data);
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) { console.error(error); }
  };

  const handleSubmitEmployee = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/payroll/employees`, form, { withCredentials: true });
      setShowForm(false);
      setForm({ name: '', role: 'Technician', hourly_rate: 0, phone: '', email: '' });
      fetchEmployees();
    } catch (error) { alert(error.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleSubmitTimeEntry = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/payroll/time-entries`, timeForm, { withCredentials: true });
      setShowTimeForm(false);
      setTimeForm({ employee_id: '', date: new Date().toISOString().split('T')[0], hours: 0, description: '', client_id: '', billable: true });
      fetchTimeEntries();
    } catch (error) { alert(error.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await axios.delete(`${API_URL}/api/payroll/time-entries/${id}`, { withCredentials: true });
      fetchTimeEntries();
    } catch (error) { alert('Error'); }
  };

  const totalHours = timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const billableHours = timeEntries.filter(e => e.billable).reduce((sum, e) => sum + (e.hours || 0), 0);
  const totalPayroll = timeEntries.reduce((sum, e) => sum + ((e.hours || 0) * (e.hourly_rate || 0)), 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll</h1>
          <p className="text-slate-500">Gestion de empleados, horas y pagos</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Empleados</p>
          <p className="text-2xl font-bold text-slate-900">{employees.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Horas Totales</p>
          <p className="text-2xl font-bold text-blue-600">{totalHours.toFixed(1)}h</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Horas Facturables</p>
          <p className="text-2xl font-bold text-green-600">{billableHours.toFixed(1)}h</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Costo Nomina</p>
          <p className="text-2xl font-bold text-amber-600">${totalPayroll.toLocaleString()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setActiveTab('employees')}
          className={`px-4 py-2.5 font-medium text-sm ${activeTab === 'employees' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500'}`}
          data-testid="tab-employees">Empleados</button>
        <button onClick={() => setActiveTab('time')}
          className={`px-4 py-2.5 font-medium text-sm ${activeTab === 'time' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500'}`}
          data-testid="tab-time-entries">Registro de Horas</button>
      </div>

      {activeTab === 'employees' && (
        <div className="space-y-4">
          <button onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            data-testid="add-employee-button">
            <Plus size={20} /> Nuevo Empleado
          </button>

          {showForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <form onSubmit={handleSubmitEmployee} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Nombre *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                    className="form-input" required data-testid="employee-name-input" />
                </div>
                <div>
                  <label className="form-label">Rol</label>
                  <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="form-input">
                    <option value="Technician">Tecnico</option>
                    <option value="Installer">Instalador</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Admin">Administrativo</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Tarifa/Hora ($)</label>
                  <input type="number" value={form.hourly_rate} onChange={(e) => setForm({...form, hourly_rate: parseFloat(e.target.value) || 0})}
                    className="form-input" min="0" step="0.01" data-testid="employee-rate-input" />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="form-input" />
                </div>
                <div>
                  <label className="form-label">Telefono</label>
                  <input type="text" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="form-input" />
                </div>
                <div className="flex items-end gap-3">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-slate-100 rounded-lg">Cancelar</button>
                  <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                    data-testid="save-employee-button">{saving ? 'Guardando...' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full" data-testid="employees-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Nombre</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Rol</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase text-slate-500">Tarifa/Hora</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Contacto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map(emp => (
                  <tr key={emp.id} className="table-row-hover">
                    <td className="px-6 py-4 font-medium">{emp.name}</td>
                    <td className="px-6 py-4 text-slate-600">{emp.role}</td>
                    <td className="px-6 py-4 text-right font-semibold">${emp.hourly_rate}/h</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{emp.email || emp.phone || '-'}</td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-400">No hay empleados registrados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'time' && (
        <div className="space-y-4">
          <button onClick={() => setShowTimeForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            data-testid="add-time-entry-button">
            <Plus size={20} /> Registrar Horas
          </button>

          {showTimeForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <form onSubmit={handleSubmitTimeEntry} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Empleado *</label>
                  <select value={timeForm.employee_id} onChange={(e) => setTimeForm({...timeForm, employee_id: e.target.value})}
                    className="form-input" required data-testid="time-employee-select">
                    <option value="">Seleccionar...</option>
                    {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Fecha *</label>
                  <input type="date" value={timeForm.date} onChange={(e) => setTimeForm({...timeForm, date: e.target.value})}
                    className="form-input" required data-testid="time-date-input" />
                </div>
                <div>
                  <label className="form-label">Horas *</label>
                  <input type="number" value={timeForm.hours} onChange={(e) => setTimeForm({...timeForm, hours: parseFloat(e.target.value) || 0})}
                    className="form-input" min="0.25" step="0.25" required data-testid="time-hours-input" />
                </div>
                <div>
                  <label className="form-label">Cliente (opcional)</label>
                  <select value={timeForm.client_id} onChange={(e) => setTimeForm({...timeForm, client_id: e.target.value})}
                    className="form-input" data-testid="time-client-select">
                    <option value="">Interno</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Descripcion</label>
                  <input type="text" value={timeForm.description} onChange={(e) => setTimeForm({...timeForm, description: e.target.value})}
                    className="form-input" data-testid="time-description-input" />
                </div>
                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={timeForm.billable}
                      onChange={(e) => setTimeForm({...timeForm, billable: e.target.checked})} className="rounded" />
                    <span className="text-sm">Facturable</span>
                  </label>
                  <button type="button" onClick={() => setShowTimeForm(false)} className="px-4 py-2 bg-slate-100 rounded-lg">Cancelar</button>
                  <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                    data-testid="save-time-entry-button">{saving ? 'Guardando...' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full" data-testid="time-entries-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Fecha</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Empleado</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Descripcion</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase text-slate-500">Cliente</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase text-slate-500">Horas</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase text-slate-500">Costo</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase text-slate-500">Acc.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {timeEntries.map(entry => (
                  <tr key={entry.id} className="table-row-hover">
                    <td className="px-6 py-4 text-sm">{new Date(entry.date).toLocaleDateString('es-ES')}</td>
                    <td className="px-6 py-4 font-medium">{entry.employee_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{entry.description || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{entry.client_name || 'Interno'}</td>
                    <td className="px-6 py-4 text-right">{entry.hours}h</td>
                    <td className="px-6 py-4 text-right font-semibold">${((entry.hours || 0) * (entry.hourly_rate || 0)).toLocaleString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleDeleteEntry(entry.id)} className="p-1 hover:bg-red-50 rounded text-red-500">
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {timeEntries.length === 0 && (
                  <tr><td colSpan="7" className="px-6 py-12 text-center text-slate-400">No hay registros de horas</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
